import en from './resources/en.json';
import ja from './resources/ja.json';
import vi from './resources/vi.json';
import type { LanguageCode, TranslationDict, Translator } from './types';

const RESOURCES: Record<LanguageCode, TranslationDict> = {
  vi: vi as TranslationDict,
  en: en as TranslationDict,
  ja: ja as TranslationDict,
};

type Leaves<T> = T extends string
  ? ''
  : {
      [K in keyof T & string]: T[K] extends string ? `${K}` : `${K}.${Leaves<T[K]>}`;
    }[keyof T & string];

export type TranslationKey = Leaves<typeof vi>;

function lookup(dict: TranslationDict, key: string): string | null {
  const parts = key.split('.');
  let current: unknown = dict;
  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = (current as Record<string, unknown>)[part];
    } else {
      return null;
    }
  }
  return typeof current === 'string' ? current : null;
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

export function createTranslator(language: LanguageCode): Translator {
  return (key, params) => {
    const primary = lookup(RESOURCES[language], key);
    if (primary !== null) return interpolate(primary, params);
    const fallback = lookup(RESOURCES.vi, key);
    if (fallback !== null) {
      if (typeof console !== 'undefined') {
        console.warn(`[i18n] Missing key "${key}" for "${language}", falling back to vi.`);
      }
      return interpolate(fallback, params);
    }
    if (typeof console !== 'undefined') {
      console.warn(`[i18n] Missing key "${key}" in all resources.`);
    }
    return key;
  };
}

export function detectBrowserLanguage(): LanguageCode {
  if (typeof navigator === 'undefined') return 'vi';
  const code = navigator.language.slice(0, 2).toLowerCase();
  if (code === 'en' || code === 'ja' || code === 'vi') return code;
  return 'vi';
}

export function detectLanguageFromQuery(search: string): LanguageCode | null {
  const params = new URLSearchParams(search);
  const lang = params.get('lang');
  if (lang === 'en' || lang === 'ja' || lang === 'vi') return lang;
  return null;
}
